import { FormattingRule } from "@/types/insights"
import { CaseColumn } from "@/types/caseColumns"
import { IconStyling } from "@/types"

import { dataTypeTests } from "@/types/insights/operators"

export const cellFormatting = {
	cellStyle (formattingRules: FormattingRule[], value: any, field: CaseColumn) {
		const style = {} as any

		const styleRules = formattingRules.filter(r => r.setBackgroundColor || r.setTextColor)
		for (let i = 0; i < styleRules.length; i++) {
			const rule = styleRules[i]

			if ((rule.setBackgroundColor && !style.backgroundColor) ||
				(rule.setTextColor && !style.color)) {
				if (this.ruleApplies(rule, value, field)) {
					if (rule.setBackgroundColor && !style.backgroundColor) {
						style.backgroundColor = rule.backgroundColor + " !important"
					}
					if (rule.setTextColor && !style.color) {
						style.color = rule.textColor
					}

					if (!!style.backgroundColor && !!style.color) {
						return style
					}
				}
			}
		}

		return style
	},

	cellBarStyle (
		formattingRules: FormattingRule[],
		rows: { [key: string]: any }[],	// All rows in the table.
		row: { [key: string]: any }, 	// The row we're currently getting styling for...
		cell: any, 						// ...the current cell...
		column: CaseColumn	 			// ...and the column.
	) {
		const style = {
			display: "none"
		} as any

		if (!rows || !row || !column) {
			return style
		}

		// Get only the rules that have bar-backgrounds enabled.
		const barRules = formattingRules.filter(r => r.setBar)

		// Loop through each bar-background rule for the column. There may be more than one.
		for (let i = 0; i < barRules.length; i++) {
			const barRule = barRules[i]

			// Check that...
			if (this.ruleApplies(barRule, cell, column) && 						// ...the rule applies (i.e. meets the condition of the rule),
				!style.backgroundColor && 										// that a background colour rule hasn't already been set,
				(barRule.barMaximum || barRule.setBarMaximum === "maximum")		// and that the maximum isn't null (or that the max setting is set to use the maximum value from the column).
			) {
				// Set bar maximum based on formatting rules.
				let maximum = null
				if (barRule.setBarMaximum === "constant") {
					maximum = barRule.barMaximum
				} else if (barRule.setBarMaximum === "relative" && barRule.barMaximum) {
					if (typeof (row[barRule.barMaximum]) === "number") {
						maximum = row[barRule.barMaximum]
					} else {
						maximum = row[barRule.barMaximum].value
					}
				} else if (barRule.setBarMaximum === "maximum") {
					// Get an array of all values for the current column, excluding any totals columns.
					const columnValues = rows.filter(r => r.label !== "Total").map(r => r[column.key])

					// Find the largest column value.
					for (i = 0; i < columnValues.length; i++) {
						if (typeof (columnValues[i]) === "number") {
							if (columnValues[i] > maximum) { maximum = columnValues[i] }
						} else {
							if (columnValues[i].value > maximum) { maximum = columnValues[i].value }
						}
					}
				}

				if (maximum !== null || maximum !== undefined) {
					let percent = parseInt(((cell / maximum) * 100).toFixed(2))
					percent = percent > 100 ? 100 : percent

					style.backgroundColor = barRule.barColor
					style.width = `calc(${percent}% - 8px)`

					delete style.display
				}

				return style
			}
		}

		return style
	},

	/**
	 * Gets the icon for the first rule that applies where the icon is set
	 * @param formattingRules The array of rules to test against
	 * @param value The value to test against all rules
	 * @param field The field we are testing against
	 * @return The icon if found, empty object otherwise
	 */
	icon (formattingRules: FormattingRule[], value: any, field: CaseColumn): IconStyling {
		const matchedRule = formattingRules.find((rule) => rule.setIcon && this.ruleApplies(rule, value, field))

		if (matchedRule) {
			return {
				class: matchedRule.icon,
				style: {
					color: (matchedRule.iconColor ?? undefined)
				}
			}
		}

		return {}
	},

	/**
	 * Tests if the specified rule applies to the specified value
	 * Returns true if the rule is satisfied, false otherwise
	 * @param rule The rule to test against
	 * @param value The value to test against the rule
	 * @param field The field we are testing against
	 */
	ruleApplies (rule: FormattingRule, value: any, field: CaseColumn): boolean {
		if (rule.operator === null || rule.operator === undefined) {
			return true
		}

		if (dataTypeTests[field.fieldDataType!] && dataTypeTests[field.fieldDataType!]![rule.operator!]) {
			return dataTypeTests[field.fieldDataType!]![rule.operator!]!(rule.value, value)
		}

		return false
	}
}
